<template>
  <ProductDetailsModal
    v-if="productDetailsModalVisible"
    :job-id="detailsJobId"
    @closeModal="productDetailsModalVisible = false"
  />
  <IngredientDetailsModal
    v-if="ingredientDetailsModalVisible"
    :job-id="detailsJobId"
    @closeModal="ingredientDetailsModalVisible = false"
  />
  <div class="submissions-table" data-cy="submissions-table">
    <div
      class="grid-control__container"
      data-cy="submissions-table-control-container"
    >
      <div class="grid-control__container-date-wrapper">
        <DateRangePicker
          class="grid-control__container-date-picker"
          :value="dateRange"
          @change="handleChange"
          data-cy="date-range-picker"
        />

        <Button
          :theme-color="'primary'"
          type="button"
          class="button-by-date"
          @click="filterByDate"
          data-cy="by-date"
          >By Date</Button
        >
      </div>
      <div class="pull-right">
        <ClearFiltersButton @clearFiltersClicked="clearFilters" />
      </div>
    </div>
    <SubmissionsGrid
      :submissionJobs="submissionJobs"
      :filter="filter"
      :loading="submissionJobsLoading"
      :skip="skip"
      :pageSize="take"
      :total="totalSubmissionJobs"
      :sort="sortDescriptors"
      :getPropValues="getPropValuesAsync"
      @detailsClicked="onDetailsClicked"
      @submissionsChanged="onSubmissionsChanged"
      @sortChanged="onSortChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="filter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { computed, onBeforeMount, ref } from "vue";
import SubmissionsGrid from "./SubmissionsGrid.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { useMainStore } from "@/stores/useMainStore";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import ProductDetailsModal from "./ProductDetailsModal.vue";
import IngredientDetailsModal from "./IngredientDetailsModal.vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import ClearFiltersButton from "@/shared/ui/inputs/ClearFiltersButton.vue";
import {
  dateTimeConvert,
  dateTimeConvertBack,
  FilterValuesTake,
  getJsonFilterString,
  valueFilterConvert,
} from "@/shared/helpers/filtersHelper";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import type { SelectionRange } from "@progress/kendo-vue-dateinputs";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import type { GridFilterChangeEvent } from "@progress/kendo-vue-grid";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import type { GetSubmissionsWithFilteringRequest } from "@/api/submissionsOverview";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import moment from "moment";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import DateRangePicker from "@/shared/ui/inputs/DateRangePicker.vue";

const main = useMainStore();

const submissionJobs = ref<SubmissionsTable[]>([]);
const totalSubmissionJobs = ref<number>(0);
const submissionJobsLoading = ref<boolean>(false);
const skip = ref<number>(0);
const take = ref<number>(100);

const productDetailsModalVisible = ref<boolean>(false);
const ingredientDetailsModalVisible = ref<boolean>(false);
const detailsJobId = ref<string>("");

const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);

const tableName = "Submissions";

const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const filter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor],
});

const valueFiltersConvertOptions = [
  {
    column: "DateProcessedForSort",
    convert: dateTimeConvert,
    convertBack: dateTimeConvertBack,
  },
] as ValueFieldConvertOption[];

const currentDate = new Date();
const startDate = moment().subtract(1, "month").toDate();

const dateRange = ref({
  start: new Date(startDate),
  end: new Date(currentDate),
} as SelectionRange);

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});


const handleChange = function (newRange: SelectionRange) {
  dateRange.value = newRange;
};

const getSubmissionsWithFilteringAsync = async function (
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    beginDate: dateRange.value.start?.toLocaleDateString("en-US"),
    endDate: dateRange.value.end?.toLocaleDateString("en-US"),
    filters: getJsonFilterString(filter.value, valueFiltersConvertOptions),
    take: take.value,
    skip: skip.value,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
    iarLogin: "",
  } as GetSubmissionsWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetSubmissionsWithFiltering", requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getAsync(createCacheKey(requestParams), () =>
    api.get("GetSubmissionsWithFiltering", requestParams)
  );
};

const getSubmissionsAsync = async function () {
  if (submissionJobsLoading.value) return;
  submissionJobsLoading.value = true;

  await handleResponsePromiseAsync(
    getSubmissionsWithFilteringAsync(),
    (data) => {
      submissionJobs.value = data.ListItems;
      totalSubmissionJobs.value = data.TotalItemsInList;
    },
    "SubmissionsTable.vue",
    "getSubmissionsAsync"
  );

  submissionJobsLoading.value = false;
};

const getPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: SubmissionsTable[] = [];
  await handleResponsePromiseAsync(
    getSubmissionsWithFilteringAsync(column.field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
    },
    "SubmissionsTable.vue",
    "getPropValuesAsync"
  );
  return result;
};

const onSubmissionsChanged = function (
  skipValue: number,
  takeValue: number,
  filterEvent: GridFilterChangeEvent | null
) {
  if (filterEvent) {
    filter.value.filters = filterEvent.filter.filters;
  }
  if (take.value != takeValue) {
    take.value = takeValue;
    GridOptionsService.savePageSizeAsync(tableName, takeValue);
  }
  skip.value = skipValue;
  getSubmissionsAsync();
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  skip.value = 0;
  getSubmissionsAsync();
};

const onDetailsClicked = function (submissions: SubmissionsTable) {
  detailsJobId.value = submissions.JobId;
  if (submissions.JobType.includes("Products")) {
    productDetailsModalVisible.value = true;
  }
  if (submissions.JobType.includes("Ingredients")) {
    ingredientDetailsModalVisible.value = true;
  }
};

const filterByDate = function () {
  getSubmissionsAsync();
};

const clearFilters = function () {
  console.debug("SubmissionsTable.vue, clearFilters()");
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  sortField.value = "";
  sortAscending.value = false;
  getSubmissionsAsync();
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) take.value = pageSize;
  await getSubmissionsAsync();
});
</script>

<style lang="scss">
.submissions-table {
  .grid-control__container {
    display: flex;

    &-left,
    &-date-wrapper {
      display: flex;
      gap: 16px;
      align-items: flex-end;
    }

    &-date-picker {
      input {
        border: unset;
      }
    }

    .button-by-date {
      height: 36px;
    }
  }

  .pull-right {
    display: flex;
    margin-left: auto;
    gap: 24px;
    align-items: flex-end;
  }

  .form-link {
    text-decoration: none;
  }
}
</style>
